import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import healthApi from '@/api/healthApi';
import {
  initDB,
  addToOfflineQueue,
  getOfflineQueue,
  removeFromOfflineQueue,
  cacheData,
  getCachedData
} from '@/utils/offlineStorage';

const OfflineContext = createContext();

export const useOffline = () => {
  const context = useContext(OfflineContext);
  if (!context) {
    throw new Error('useOffline must be used within OfflineProvider');
  }
  return context;
};

export const OfflineProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const [queueSize, setQueueSize] = useState(0);

  // Track whether we've already shown the "you're offline" toast in the
  // current session — prevents toast spam on rapid flaps and on initial
  // page load while still offline (the persistent banner already covers it).
  const offlineToastShownRef = useRef(false);

  // Initialize IndexedDB
  useEffect(() => {
    initDB().catch(err => {
      console.error('Failed to initialize offline DB:', err);
    });
  }, []);

  // Update queue size
  const updateQueueSize = useCallback(async () => {
    try {
      const queue = await getOfflineQueue();
      setQueueSize(queue.length);
    } catch (error) {
      console.error('Failed to get queue size:', error);
    }
  }, []);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = async () => {
      setIsOnline(true);
      offlineToastShownRef.current = false;
      const queue = await getOfflineQueue();
      // Only surface a recovery toast if there's actually something to sync;
      // otherwise the persistent indicator's "Back online" pill is enough.
      if (queue.length > 0) {
        toast.success('Back online — syncing your updates.', { duration: 2500 });
        await syncOfflineData();
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
      if (!offlineToastShownRef.current) {
        offlineToastShownRef.current = true;
        toast('Offline mode active. We\'ll sync once you reconnect.', {
          duration: 3500,
        });
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check queue size on mount
    updateQueueSize();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [updateQueueSize]);

  // Sync offline data when connection is restored
  const syncOfflineData = useCallback(async () => {
    if (isSyncing) return;

    setIsSyncing(true);

    try {
      const queue = await getOfflineQueue();
      let successCount = 0;
      let failCount = 0;

      for (const item of queue) {
        try {
          await processOfflineAction(item);
          await removeFromOfflineQueue(item.id);
          successCount++;
        } catch (error) {
          // Failures stay in queue and retry on the next online event.
          console.error('Sync attempt failed for action:', item.type);
          failCount++;
        }
      }

      await updateQueueSize();

      if (successCount > 0) {
        toast.success(
          successCount === 1
            ? 'Update synced.'
            : `${successCount} updates synced.`,
          { duration: 2500 },
        );
      }

      if (failCount > 0 && successCount === 0) {
        // Calm wording — no "queue failed" or "retry pending" technical talk.
        toast(`Some updates will sync next time you're online.`, {
          duration: 3500,
        });
      }
    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      setIsSyncing(false);
    }
  }, [isSyncing, updateQueueSize]);

  // Process individual offline action
  const processOfflineAction = async (action) => {
    const { type, data } = action;

    switch (type) {
      case 'TOGGLE_TASK':
        await healthApi.toggleDailyTask(data.taskId);
        break;
      
      case 'CREATE_CHECKIN':
        await healthApi.createCheckIn(data);
        break;
      
      case 'TRACK_HABIT':
        await healthApi.trackHabit(data);
        break;
      
      case 'TRACK_MEAL_SWAP':
        await healthApi.trackMealSwap();
        break;
      
      case 'CREATE_MEDICATION':
        await healthApi.createMedication(data);
        break;
      
      case 'DELETE_MEDICATION':
        await healthApi.deleteMedication(data.medicationId);
        break;
      
      case 'MARK_NOTIFICATION_READ':
        await healthApi.markNotificationRead(data.notificationId);
        break;
      
      case 'UPDATE_MEAL_PREFERENCES':
        await healthApi.updateMealPreferences(data);
        break;
      
      case 'WELLNESS_CHECKIN':
        await healthApi.createWellnessCheckIn(data);
        break;
      
      default:
        console.warn('Unknown action type:', type);
    }
  };

  // Queue an action for offline sync
  const queueOfflineAction = useCallback(async (type, data) => {
    try {
      await addToOfflineQueue({ type, data });
      await updateQueueSize();
      toast('Saved offline — syncing when online.', {
        duration: 2000,
      });
    } catch (error) {
      console.error('Failed to queue action:', error);
      toast('Could not save offline. Try again in a moment.');
    }
  }, [updateQueueSize]);

  // Cache API response
  const cacheAPIData = useCallback(async (key, data) => {
    try {
      await cacheData(key, data);
    } catch (error) {
      console.error('Failed to cache data:', error);
    }
  }, []);

  // Get cached API data
  const getCachedAPIData = useCallback(async (key) => {
    try {
      return await getCachedData(key);
    } catch (error) {
      console.error('Failed to get cached data:', error);
      return null;
    }
  }, []);

  const value = {
    isOnline,
    isSyncing,
    queueSize,
    queueOfflineAction,
    syncOfflineData,
    cacheAPIData,
    getCachedAPIData
  };

  return (
    <OfflineContext.Provider value={value}>
      {children}
    </OfflineContext.Provider>
  );
};

export default OfflineContext;
